import { AxiosRequestConfig, AxiosResponse, CanceledError } from 'axios';
import type { Nullable } from '../../../types';
import { AuthRepository } from '../../repositories/authRepository';
import { handleErrors } from '../../../helpers';
import AxiosClient from './axiosClient';

export default class AuthAxiosClient extends AxiosClient {
  authRepository: AuthRepository;

  constructor(baseURL: string, authRepository: AuthRepository) {
    super(baseURL);
    this.authRepository = authRepository;
    this.createRequestInterceptor();
    this.createResponseInterceptor();
  }

  createRequestInterceptor(): void {
    const { httpClient, authRepository } = this;
    httpClient.interceptors.request.use(
      (configuration) => {
        const accessToken: Nullable<string> = authRepository.getAccessToken();
        if (accessToken == null) return configuration;
        return this.configurationWithAuthorizationHeader(
          accessToken,
          configuration
        );
      },
      (error) => Promise.reject(error)
    );
  }

  createResponseInterceptor(): void {
    const { httpClient } = this;
    httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error instanceof CanceledError) {
          return Promise.resolve({ isCanceled: true });
        }
        // Reject promise if usual error
        if (error?.response?.status !== 401) {
          return Promise.reject(error);
        }

        const { config } = error;
        return this.refreshTokenAndResendRequest(config);
      }
    );
  }

  // eslint-disable-next-line class-methods-use-this
  configurationWithAuthorizationHeader(
    accessToken: string,
    configuration: AxiosRequestConfig
  ): AxiosRequestConfig {
    const config = configuration;
    // @ts-ignore
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  }

  refreshTokenAndResendRequest(
    configuration: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const { httpClient, authRepository } = this;
    return authRepository
      .updateToken()
      .then((accessToken) => {
        const config = this.configurationWithAuthorizationHeader(
          accessToken,
          configuration
        );
        return httpClient.request(config);
      })
      .catch((err) => {
        const error = handleErrors(err);
        return Promise.reject(error);
      })
      .finally(() => this.createRequestInterceptor());
  }
}
